import CaseCoCounselModal from "@/components/cases/case-co-counsel-modal";
import CaseCreateAttachments from "@/components/cases/case-create-attachments";
import CaseDefendantModal from "@/components/cases/case-defendant-modal";
import BlockBackNavigation from "@/components/common/block-back-navigation";
import BottomBar from "@/components/core/actions/bottombar";
import Alert from "@/components/core/alert";
import { Blanket, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { Checkbox } from "@/components/core/forms/checkbox";
import Combobox from "@/components/core/forms/combobox";
import DragDropFile from "@/components/core/forms/drag-drop-file";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage, FormPrepend } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import { useModal } from "@/components/core/modals/use-modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import Tooltip from "@/components/core/tooltip";
import CounselInfoModal from "@/components/counsel-info-modal";
import { collectionTypes } from "@/constants/collection-types";
import { useEvidenceMutation } from "@/features/case-evaluation/api/queries/evidence-query.ts";
import { useAuth } from "@/hooks/use-auth";
import { useNavigationBlocker } from "@/hooks/use-navigation-blocker";
import { useValidationError } from "@/hooks/use-validation-errors";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconHelpCircle, IconPlus } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useCaseRecordMutation } from "../case-record/api/queries/case-records-query";
import type { CaseRecord } from "../case-record/types/case-records";
import caseCategoryService from "../dashboard/api/services/case-category-service";
import caseTypeService from "../dashboard/api/services/case-type-service";
import cityService from "../dashboard/api/services/city-service";
import courtLevelService from "../dashboard/api/services/court-level-service";
import courtTypeService from "../dashboard/api/services/court-type-service";
import courtTypeStationService from "../dashboard/api/services/court-type-station-service";
import provinceService from "../dashboard/api/services/province-service";
import regionService from "../dashboard/api/services/region-service";
import userService from "../dashboard/api/services/user-service";
import { type CaseFilingFormSchema, type CaseFilingOccFormSchema, caseFilingFormSchema, caseFilingOccFormSchema } from "../dashboard/types/case-type";
import EvidenceList from "./components/evidence-list";
import type { Counsel } from "./types/counsel";
import type { Defendant } from "./types/defendant";

export default function CaseInfo({
	data,
}: {
	data?: CaseRecord;
}) {
	const counselColumns: ColumnDef<Counsel>[] = [
		{
			id: "counsel_name",
			label: "Counsel Name",
			cellClassName: "w-[350px] cursor-pointer",
			cell: ({ data, rowIndex, onClick }) => {
				return (
					<button type="button" className="flex flex-row items-center w-full p-2 gap-1 bg-white">
						<span className="font-semibold">
							{`${data.first_name} ${data.middle_name ? `${data.middle_name} ` : ""}${data.last_name}`} {data.suffix}
						</span>
					</button>
				);
			},
		},
		{
			id: "counsel_law_firm",
			label: "Law Firm",
			cellClassName: "w-[200px]",
			cell: ({ data }) => data.law_firm || "n/a",
		},
		{
			id: "counsel_law_firm_email",
			label: "Law Firm Email",
			cellClassName: "w-[200px]",
			cell: ({ data }) => data.law_firm_email || "n/a",
		},
		{
			id: "counsel_email",
			label: "Email Address",
			cellClassName: "w-[200px]",
			cell: ({ data }) => data.email,
		},
		// {
		// 	id: "counsel_address",
		// 	label: "Address",
		// 	cellClassName: "uppercase",
		// 	cell: ({ data }) =>
		// 		data.address_1 || data.address_2 || data.barangay || data?.city?.name || data?.province?.name || data.zipcode
		// 			? `${data.address_1 || ""} ${data.address_2 || ""} ${data.barangay || ""} ${data?.city_name || ""} ${data?.province_name || ""} ${data.zipcode || ""}`.trim()
		// 			: "--",
		// },
	];

	const defendantColumns: ColumnDef<Defendant>[] = [
		{
			id: "first_name",
			label: "Defendant Name",
			cellClassName: "w-[350px] cursor-pointer",
			cell: ({ data, rowIndex, onClick }) => {
				return (
					<button onClick={() => onClick?.(data, rowIndex)} type="button" className="flex flex-row items-center w-full p-2 gap-1 bg-white hover:bg-slate-100">
						{data.classification === "Individual" && (
							<span className="font-semibold text-blue-600 hover:text-blue-500">
								{`${data.first_name} ${data.middle_name ? `${data.middle_name} ` : ""}${data.last_name}`}
							</span>
						)}

						{(data.classification === "Juridical" || data.classification === "Government Agency") && (
							<span className="font-semibold text-blue-600 hover:text-blue-500">{`${data.corporation}`}</span>
						)}
					</button>
				);
			},
		},
		{
			id: "classification",
			label: "Type",
			cellClassName: "w-[350px] cursor-pointer",
			cell: ({ data }) => {
				return <span className="font-semibold">{data.classification}</span>;
			},
		},
		{
			id: "address_1",
			label: "Address",
			cell: ({ data }) =>
				data.address_1 || data.address_2 || data.barangay || data?.city?.name || data?.province?.name || data.zipcode
					? `${data.address_1 || ""} ${data.address_2 || ""} ${data.barangay || ""} ${data?.city?.name || ""} ${data?.province?.name || ""} ${data.zipcode || ""}`.trim()
					: "--",
		},
	];

	const plaintiffColumns: ColumnDef<Defendant>[] = [
		{
			id: "first_name",
			label: "Name",
			cellClassName: "w-[350px] cursor-pointer",
			cell: ({ data, rowIndex, onClick }) => {
				return (
					<button onClick={() => onClick?.(data, rowIndex)} type="button" className="flex flex-row items-center w-full p-2 gap-1 bg-white hover:bg-slate-100">
						{data.classification === "Individual" && (
							<span className="font-semibold text-blue-600 hover:text-blue-500">
								{`${data.first_name} ${data.middle_name ? `${data.middle_name} ` : ""}${data.last_name}`}
							</span>
						)}

						{(data.classification === "Juridical" || data.classification === "Government Agency") && (
							<span className="font-semibold text-blue-600 hover:text-blue-500">{`${data.corporation}`}</span>
						)}
					</button>
				);
			},
		},
		{
			id: "classification",
			label: "Type",
			cellClassName: "w-[350px] cursor-pointer",
			cell: ({ data }) => {
				return <span className="font-semibold">{data.classification}</span>;
			},
		},
		{
			id: "address_1",
			label: "Address",
			cell: ({ data }) =>
				data.address_1 || data.address_2 || data.barangay || data?.city?.name || data?.province?.name || data.zipcode
					? `${data.address_1 || ""} ${data.address_2 || ""} ${data.barangay || ""} ${data?.city?.name || ""} ${data?.province?.name || ""} ${data.zipcode || ""}`.trim()
					: "--",
		},
	];

	const { user } = useAuth();
	const bcc = user?.is_bcc === true;

	const modal = useModal();
	type CaseFormType = CaseFilingFormSchema | CaseFilingOccFormSchema;
	const form = useForm<CaseFormType>({
		resolver: zodResolver(bcc ? caseFilingFormSchema : caseFilingOccFormSchema),
		mode: "onChange",
		defaultValues: {
			classification: "Individual",
			case_title: "",
			with_fee_exemption: false,
			summons_count: "1",
			court_level: "2ND_LEVEL_COURT",
			court_type: "RTC",
			region_code: "NCR",
			defendants: [],
			plaintiffs: [],
			counsels: [],
			evidences: [],
			province_code: "MM",
			city_code: "Makati",
			address_1: "",
			address_2: "",
			counsel_email: null,
			counsel_middle_name: "",
			counsel_region_code: "",
			counsel_province_code: "",
			counsel_city_code: "",
			counsel_barangay: "",
			counsel_mobile_number: "",
			counsel_law_firm_email: null,
			counsel_address_1: "",
			counsel_address_2: "",
			counsel_zipcode: "",
			counsel_roll_number: "",
			lawfirm_region_code: "",
			lawfirm_province_code: "",
			lawfirm_city_code: "",
			lawfirm_barangay: "",
			lawfirm_address_1: "",
			lawfirm_address_2: "",
			lawfirm_zipcode: "",
			counsel_suffix: "",
			plaintiff: {
				mobile_number: "",
				landline: "",
				email: "",
				middle_name: "",
				region_code: "",
				province_code: "",
				city_code: "",
				barangay: "",
				address_1: "",
				address_2: "",
				zipcode: "",
				suffix: "",
			},
			court_station: "RTC_MAK",
			case_category: "CIVIL_CASE",
			service_type: "case_filing",
			collection_type: "case_filing",
			party_type: "Petitioner",
			case_type: "930",
			advance_information: false,
			nature_of_action: "",
			claim_amount: 0,
		},
	});

	const totalAllowedMb = 200;
	const evidences = form.watch("evidences");

	const navigate = useNavigate();
	const caseRecordMutation = useCaseRecordMutation();
	const expungeCaseMutation = useEvidenceMutation();

	const serviceType = form.watch("service_type");
	const natureOfAction = form.watch("nature_of_action");
	const corporation = form.watch("corporation");

	const [errors, _, getFirstError] = useValidationError();
	const classification = form.watch("classification");

	const caseCategory = form.watch("case_category");
	const courtLevel = form.watch("court_level");
	const courtType = form.watch("court_type");
	const courtRegionCode = form.watch("region_code");
	const courtProvince = form.watch("province_code");

	const lawfirmRegionCode = form.watch("lawfirm_region_code");
	const lawfirmProvinceCode = form.watch("lawfirm_province_code");

	const [payment, setPayments] = useState<File[]>([]);
	const [caseClassification, setCaseClassification] = useState(null);
	const [hasCounsel, setHasCounsel] = useState(true);
	const [files, setFiles] = useState<File[]>([]);
	const [annexFiles, setAnnexFiles] = useState<File[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [debouncedEmail, setDebouncedEmail] = useState("");
	const [maxSizeExceeded, setMaxSize] = useState(false);
	const [totalMaxSize, setTotalMaxSize] = useState(0);
	const [emailExists, setEmailExists] = useState(false);
	const email = form.watch("counsel_email");
	const firstName = form.watch("counsel_first_name");
	const branch = form.watch("branch_id");

	const updateDebouncedEmail = useCallback(
		debounce(
			(newEmail) => {
				setDebouncedEmail(newEmail);
			},
			500,
			{ trailing: true },
		),
		[],
	);

	useEffect(() => {
		updateDebouncedEmail(email);
	}, [email, updateDebouncedEmail]);

	const [defendantLabel, setDefendantLabel] = useState("Defendant");
	const [plaintiffLabel, setPlaintiffLabel] = useState("Plaintiff");

	useEffect(() => {
		const evidences = form.watch("evidences");
		const totalSizeMb = [...files, ...payment, ...evidences].reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
		if (totalSizeMb >= totalAllowedMb) {
			setMaxSize(true);
		} else {
			setMaxSize(false);
		}
		setTotalMaxSize(totalSizeMb);
	}, [files, payment, form.watch("evidences")]);

	// useEffect(() => {
	// 	if (caseClassification === "Small Claims") {
	// 		setPlaintiffLabel("Petitioner");
	// 		setDefendantLabel("Respondent");
	// 	}
	// }, [caseClassification]);

	// useEffect(() => {
	// 	if (debouncedEmail) {
	// 		userService
	// 			.validateEmail(debouncedEmail)
	// 			.then((response) => {
	// 				if (response) {
	// 					const user = response;
	// 					setEmailExists(true);
	// 					form.setValue("counsel_law_firm", user.law_firm || "");
	// 					form.setValue("counsel_mobile_number", user.contact || "");
	// 					form.setValue("counsel_first_name", user.first_name);
	// 					form.setValue("counsel_middle_name", user.middle_name || "");
	// 					form.setValue("counsel_last_name", user.last_name);
	// 					form.setValue("counsel_address_1", user.address_1 || "");
	// 					form.setValue("counsel_address_2", user.address_2 || "");
	// 					form.setValue("counsel_barangay", user.barangay || "");
	// 					form.setValue("counsel_city_code", user.city_code || "");
	// 					form.setValue("counsel_province_code", user.province_code || "");
	// 					form.setValue("counsel_region_code", user.region_code || "");
	// 					form.setValue("counsel_zipcode", user.zipcode || "");
	// 				} else {
	// 					setEmailExists(false);
	// 				}
	// 			})
	// 			.catch((_) => {
	// 				setEmailExists(false);
	// 			});
	// 	}
	// }, [debouncedEmail, form]);

	const counsels = useFieldArray({
		control: form.control,
		name: "counsels",
	});

	const plaintiffs = useFieldArray({
		control: form.control,
		name: "plaintiffs",
		keyName: "id",
	});

	const defendants = useFieldArray({
		control: form.control,
		name: "defendants",
		keyName: "id",
	});
	const [counselLawFirmRegionName, setCounselLawFirmRegionName] = useState<string | null>(null);
	const [counselLawFirmProvinceName, setCounselLawFirmProvinceName] = useState<string | null>(null);
	const [counselLawFirmCityName, setCounselLawFirmCityName] = useState<string | null>(null);
	const lawFirm = form.watch("counsel_law_firm");
	const counselLawFirmEmail = form.watch("counsel_law_firm_email");
	const counselLawFirmRegion = form.watch("lawfirm_region_code");
	const counselLawFirmProvince = form.watch("lawfirm_province_code");
	const counselLawFirmCity = form.watch("lawfirm_city_code");
	const counselLawFirmBarangay = form.watch("lawfirm_barangay");
	const counselLawFirmAddress1 = form.watch("lawfirm_address_1");
	const counselLawFirmAddress2 = form.watch("lawfirm_address_2");
	const counselLawFirmZipcode = form.watch("lawfirm_zipcode");
	const [isDirty, setIsDirty] = useState(false);

	useNavigationBlocker(isDirty);

	const defendantEmails = defendants.fields.map((defendant) => defendant.email);
	const counselEmails = counsels.fields.map((counsel) => counsel.email);
	const allParties = [defendants.fields, ...plaintiffs.fields];
	const allEmails = [email, ...counselEmails, ...defendantEmails];
	const counselsLawFirm = counsels.fields.map((counsel) => counsel.law_firm);
	const counselsLawFirmEmail = counsels.fields.map((counsel) => counsel.law_firm_email);
	const allLawFirm = [lawFirm, ...counselsLawFirm];
	const allLawFirmEmail = [counselLawFirmEmail, ...counselsLawFirmEmail];
	const allCounsels = [
		{
			law_firm: lawFirm,
			law_firm_email: counselLawFirmEmail,
			lawfirm_region_code: counselLawFirmRegion,
			lawfirm_region_name: counselLawFirmRegionName,
			lawfirm_province_name: counselLawFirmProvinceName,
			lawfirm_city_name: counselLawFirmCityName,
			lawfirm_province_code: counselLawFirmProvince,
			lawfirm_city_code: counselLawFirmCity,
			lawfirm_barangay: counselLawFirmBarangay,
			lawfirm_address_1: counselLawFirmAddress1,
			lawfirm_address_2: counselLawFirmAddress2,
			lawfirm_zipcode: counselLawFirmZipcode,
		},
		...counsels.fields,
	];
	const oldLawFirmRef = useRef<string | null>(null);
	const oldCounselLawFirmEmail = useRef<string | null>(null);

	const debounceLawFirm = useCallback(
		debounce((value) => {
			for (let i = 0; i < counsels.fields.length; i++) {
				const counsel = counsels.fields[i];

				if (counsel.law_firm === oldLawFirmRef.current) {
					counsel.law_firm = value;
				}
			}
			oldLawFirmRef.current = value;
		}, 300),
		[],
	);

	useEffect(() => {
		debounceLawFirm(lawFirm);
		return () => debounceLawFirm.cancel();
	}, [lawFirm, debounceLawFirm]);

	const debounceLawFirmEmail = useCallback(
		debounce((value) => {
			for (let i = 0; i < counsels.fields.length; i++) {
				const counsel = counsels.fields[i];
				if (counsel.law_firm_email === oldCounselLawFirmEmail.current) {
					counsel.law_firm_email = value;
				}
			}
			oldCounselLawFirmEmail.current = value;
		}, 300),
		[],
	);

	useEffect(() => {
		if (bcc) {
			form.setValue("branch_id", user.branches?.[0]?.assignable?.id);
		}
	}, [bcc]);
	useEffect(() => {
		debounceLawFirmEmail(counselLawFirmEmail);
		return () => debounceLawFirmEmail.cancel();
	}, [counselLawFirmEmail, debounceLawFirmEmail]);

	function formatNames(names: string[]): string {
		if (names.length === 0) return "";
		if (names.length === 1) return names[0];
		if (names.length === 2) return `${names[0]} and ${names[1]}`;
		return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
	}

	useEffect(() => {
		if (classification === "Individual" && plaintiffs.fields.length && defendants.fields.length) {
			const defendantNames = formatNames(
				defendants.fields.map((defendant) =>
					defendant.classification === "Individual" ? `${defendant.first_name} ${defendant.last_name}` : defendant.corporation,
				),
			);

			const plaintiffNames = formatNames(
				plaintiffs.fields.map((plaintiff) =>
					plaintiff.classification === "Individual" ? `${plaintiff.first_name} ${plaintiff.last_name}` : plaintiff.corporation,
				),
			);

			form.setValue("case_title", `${plaintiffNames} vs ${defendantNames} - ${natureOfAction}`);
		}
	}, [
		classification,
		plaintiffs.fields.map((p) => `${p.classification}-${p.first_name}-${p.last_name}-${p.corporation}`).join("|"),
		defendants.fields.map((d) => `${d.classification}-${d.first_name}-${d.last_name}-${d.corporation}`).join("|"),
	]);

	useEffect(() => {
		if (classification === "Juridical" && corporation && defendants.fields.length) {
			const defendantNames = defendants.fields
				.map((defendant) => (defendant.classification === "Individual" ? `${defendant.first_name} ${defendant.last_name}` : defendant.corporation))
				.join(" and ");

			form.setValue("case_title", `${corporation} vs ${defendantNames} - ${natureOfAction}`);
		}
		if (classification === "Juridical" && corporation && defendants.fields.length) {
			const defendantJuridical = defendants.fields
				.map((defendant) => (defendant.classification === "Juridical" ? defendant.corporation : `${defendant.first_name} ${defendant.last_name}`))
				.join(" and ");

			form.setValue("case_title", `${corporation} vs ${defendantJuridical} - ${natureOfAction}`);
		}
	}, [classification, defendants, corporation, form]);

	const handleSubmit = async (data: any) => {
		try {
			data.payment = payment;
			data.files = files;
			data.annexes = annexFiles;

			const response = await caseRecordMutation.mutateAsync(data);

			!bcc
				? await navigate({
						to: "/case-classification/$caseClassificationId",
						params: {
							caseClassificationId: response.id,
						},
					})
				: await navigate({
						to: "/case-evaluation/$caseEvaluationId",
						params: {
							caseEvaluationId: response.id,
						},
					});
			setIsSubmitting(false);
		} catch (error) {
			setIsSubmitting(false);
		}
	};

	const handleConfirmClick = () => {
		const modalId = modal.openModal({
			type: "confirm",
			variant: "green",
			title: "Confirm Case Details",
			description: "Are you sure you want to submit this case? Please review the details before proceeding.",
			onConfirm: async () => {
				setIsSubmitting(true);
				await form.handleSubmit(handleSubmit)();
				modal.closeModal(modalId);
				setIsSubmitting(false);
			},
		});
	};

	// dialog box warning for browser refresh / close tab
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, []);

	return (
		<div className="flex flex-col w-full max-w-base p-6 gap-6">
			<BlockBackNavigation />
			<div className="flex flex-col w-full gap-3">
				<Form {...form}>
					<div className="flex flex-col w-full gap-6">
						{!!errors.length && (
							<Alert type="error" title="Case Filing Failed" className="mb-2" icon={<IconAlertCircle size={16} stroke={2} />}>
								{getFirstError()}
							</Alert>
						)}
						<div className="flex flex-col w-full gap-3">
							<div className="flex flex-col w-full">
								<span className="text-lg font-semibold text-foreground-dark">Court Details</span>
								<span className="text-base text-foreground/70">
									Select the right court details appropriate to this case. Please ensure that the selected details are correct.
								</span>
							</div>
							{!bcc ? (
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<div className="col-span-4 flex">
										<FormField
											control={form.control}
											name="court_level"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Court Level</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															placeholderValue={{
																label: "2nd Level Court",
																value: "2ND_LEVEL_COURT",
															}}
															defaultValue={field.value}
															onValueChange={(value) => {
																form.setValue("province_code", "");
																form.setValue("city_code", "");
																form.setValue("court_type", "");

																field.onChange(value);
															}}
															loadOptions={async (value: string) => {
																const items = await courtLevelService.list({
																	"filter[search]": value,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="col-span-4 flex">
										<FormField
											control={form.control}
											name="court_type"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Court Type</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															defaultValue={field.value}
															disabled={!courtLevel}
															key={courtLevel}
															placeholderValue={{
																label: "Regional Trial Court",
																value: "RTC",
															}}
															onValueChange={(value) => {
																form.setValue("province_code", "");
																form.setValue("city_code", "");

																field.onChange(value);
															}}
															loadOptions={async (value: string) => {
																const items = await courtTypeService.list({
																	"filter[search]": value,
																	"filter[court_level]": courtLevel,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="col-span-4 flex">
										<FormField
											control={form.control}
											name="region_code"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Court Region</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															key={courtType}
															disabled={!courtType}
															placeholderValue={{
																label: "NCR",
																value: "NCR",
															}}
															defaultValue={field.value}
															onValueChange={(value) => {
																form.setValue("province_code", "");
																form.setValue("city_code", "");

																field.onChange(value);
															}}
															loadOptions={async (value: string) => {
																const items = await regionService.list({
																	"filter[search]": value,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="col-span-8 flex">
										<FormField
											control={form.control}
											name="province_code"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Court Province</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															defaultValue={field.value}
															placeholderValue={{
																label: "Metro Manila",
																value: "MM",
															}}
															key={courtRegionCode}
															disabled={!courtRegionCode}
															onValueChange={(value) => {
																form.setValue("city_code", "");

																field.onChange(value);
															}}
															loadOptions={async (value: string) => {
																const items = await provinceService.list({
																	"filter[search]": value,
																	"filter[region_code]": courtRegionCode,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="col-span-4 flex">
										<FormField
											control={form.control}
											name="court_station"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Court Station/Office</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															disabled={!courtProvince}
															key={courtProvince}
															defaultValue={field.value}
															placeholderValue={{
																value: "RTC_MAK",
																label: "MAKATI CITY",
															}}
															onValueChange={field.onChange}
															loadOptions={async (value: string) => {
																const items = await courtTypeStationService.list({
																	"filter[search]": value,
																	"filter[province_code]": courtProvince,
																	"filter[court_type_code]": courtType,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>
							) : (
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<div className="col-span-4 flex">
										<FormField
											control={form.control}
											name="branch_id"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Court Branch</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Select
															onValueChange={field.onChange}
															defaultValue={field.value}
															disabled={user?.branches.length <= 1}
															options={user?.branches.map((item) => ({
																label: item.assignable?.name,
																value: item.assignable?.id,
															}))}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>
							)}
						</div>
						{serviceType === "case_filing" ? (
							<div className="flex flex-col w-full gap-3">
								<div className="flex flex-col w-full">
									<span className="text-lg font-semibold text-foreground-dark">Case Details</span>
									<span className="text-base text-foreground/70">
										Select the right case details appropriate to this case. Please ensure that the selected details are correct.
									</span>
								</div>
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									{bcc && (
										<>
											<div className="col-span-6 w-full">
												<FormField
													control={form.control}
													name="filed_at"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel required>Date Filed</FormLabel>
																<FormDescription />
															</div>
															<FormGroup iconPosition="left">
																<Input type="date" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-6 w-full">
												<FormField
													control={form.control}
													name="raffled_at"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel required>Date Raffled</FormLabel>
																<FormDescription />
															</div>
															<FormGroup iconPosition="left">
																<Input type="date" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>

											<div className="col-span-6 w-full">
												<FormField
													control={form.control}
													name="received_by"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel required>Received By</FormLabel>
																<FormDescription />
															</div>
															<FormGroup>
																<Input autoComplete="off" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-6 w-full">
												<FormField
													control={form.control}
													name="received_at"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel required>Date Received</FormLabel>
																<FormDescription />
															</div>
															<FormGroup iconPosition="left">
																<Input type="date" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>

											<div className="col-span-12 flex">
												<FormField
													control={form.control}
													name="case_number"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel required>Case Number</FormLabel>
																<FormDescription />
															</div>
															<FormGroup>
																<Input autoComplete="off" {...field} className="uppercase" onBlur={(e) => field.onChange(e.target.value.toUpperCase())} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										</>
									)}
									{branch && (
										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="case_category"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Case Category</FormLabel>
															<FormDescription />
														</div>

														<FormGroup>
															<Combobox
																defaultValue={field.value}
																onValueChange={field.onChange}
																placeholderValue={{
																	label: "Civil Cases",
																	value: "CIVIL_CASE",
																}}
																loadOptions={async (value: string) => {
																	const items = await caseCategoryService.listByBranch({
																		"filter[search]": value,
																		"filter[status]": "active",
																		branch_id: branch,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									)}
									{branch && (
										<div className="col-span-12 flex">
											<FormField
												control={form.control}
												name="case_type"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel required>Case Type</FormLabel>
															<FormDescription />
														</div>
														<FormGroup>
															<Combobox
																defaultValue={field.value}
																onValueChange={async (value) => {
																	form.setValue("case_type", value);
																	const items = await caseTypeService.listByBranch({
																		"filter[search]": value,
																		"filter[case_category_code]": caseCategory,
																		"filter[status]": "active",
																		branch_id: branch,
																	});

																	const caseType = items.data.find((type) => type.code === value);
																	setPlaintiffLabel(caseType.plaintiff_label);
																	setDefendantLabel(caseType.defendant_label);
																	setCaseClassification(caseType?.classification);
																	setHasCounsel(caseType?.has_counsels);
																}}
																placeholderValue={{
																	label: "Collection of Sum of Money",
																	value: "930",
																}}
																loadOptions={async (value: string) => {
																	const items = await caseTypeService.listByBranch({
																		"filter[search]": value,
																		"filter[case_category_code]": caseCategory,
																		branch_id: branch,
																	});

																	return items.data.map((item) => ({
																		label: item.name,
																		value: item.code,
																		classification: item.classification,
																	}));
																}}
															/>
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									)}
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="nature_of_action"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Nature of Action</FormLabel>
														<FormDescription>E.g. Breach of Contract/Specific Performance/For the Collection of Sum of Money</FormDescription>
													</div>
													<FormGroup>
														<Input autoComplete="off" {...field} className="uppercase" onBlur={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									{caseClassification === "Small Claims" && (
										<div className="col-span-4 flex">
											<FormField
												control={form.control}
												name="claim_amount"
												render={({ field }) => (
													<FormItem>
														<div className="flex flex-col w-full">
															<FormLabel>Claim Amount</FormLabel>
															<FormDescription>maximum claim amount for small claim is Php 999,999.99</FormDescription>
														</div>
														<FormGroup>
															<Input {...field} type="number" max="999999.99" />
														</FormGroup>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>
									)}
								</div>
							</div>
						) : (
							<div className="flex flex-col w-full gap-3">
								<div className="flex flex-col w-full">
									<span className="text-lg font-semibold text-foreground-dark">Application Details</span>
									<span className="text-base text-foreground/70">Brief description about this section</span>
								</div>
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="collection_type"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel required>Collection Type</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Select onValueChange={field.onChange} defaultValue={field.value} options={collectionTypes} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>
							</div>
						)}
						<div className="flex flex-col w-full gap-3">
							<div className="flex flex-col w-full">
								<span className="text-lg font-semibold text-foreground-dark">{plaintiffLabel} Information</span>
								<span className="text-base text-foreground/70">
									Provide the full details of the {plaintiffLabel}(s) of this case. This ensures proper identification of the parties involved and helps
									maintain accurate records.
								</span>
							</div>

							<div className="flex flex-col w-full rounded-md border border-border/50">
								{plaintiffs.fields.length > 0 && (
									<div className="flex flex-row items-center justify-end w-full p-3 gap-4 border-b border-border/50">
										<Button
											type="button"
											size="sm"
											onClick={() => {
												const defendantModal = modal.openModal({
													title: `Add New ${plaintiffLabel}`,
													description: `Case ${plaintiffLabel}`,
													size: "lg",
													component: (
														<CaseDefendantModal
															emails={allEmails}
															isPlaintiff={true}
															allParties={allParties}
															defendantLabel={plaintiffLabel}
															onSubmit={(data) => {
																plaintiffs.append(data);
																console.log(data);
																modal.closeModal(defendantModal);
															}}
														/>
													),
												});
											}}
										>
											<IconPlus size={14} stroke={2} />
											<span>Add {plaintiffLabel}</span>
										</Button>
									</div>
								)}

								<div className="flex flex-col w-full gap-3 border-b last:border-b-0 border-border/50">
									{plaintiffs.fields.length === 0 ? (
										<Blanket width="sm">
											<BlanketInfo
												title="No Data"
												message="No available data for now. Please check back later or refresh the page."
												action={
													<Button
														variant="default"
														size="sm"
														onClick={() => {
															const defendantModal = modal.openModal({
																title: `Add New ${plaintiffLabel}`,
																description: `Case ${plaintiffLabel}`,
																size: "lg",
																component: (
																	<CaseDefendantModal
																		emails={allEmails}
																		isPlaintiff={true}
																		allParties={allParties}
																		defendantLabel={plaintiffLabel}
																		onSubmit={(data) => {
																			plaintiffs.append(data);
																			modal.closeModal(defendantModal);
																		}}
																	/>
																),
															});
														}}
													>
														<IconPlus size={16} stroke={2} />
														Add {plaintiffLabel}
													</Button>
												}
											/>
										</Blanket>
									) : (
										<div className="col-span-12 flex flex-col w-full">
											<DataTable
												data={plaintiffs.fields}
												columns={plaintiffColumns}
												onRowClick={(data: Defendant, rowIndex: number) => {
													const defendantModal = modal.openModal({
														title: `Update ${plaintiffLabel}`,
														description: `Case ${plaintiffLabel}`,
														size: "lg",
														component: (
															<CaseDefendantModal
																data={data}
																allParties={allParties}
																emails={allEmails}
																isPlaintiff={true}
																isEdit={true}
																defendantLabel={plaintiffLabel}
																onSubmit={(updatedData) => {
																	plaintiffs.update(rowIndex, updatedData);
																	modal.closeModal(defendantModal);
																}}
															/>
														),
													});
												}}
												onRemove={(rowData: number) => {
													const index = plaintiffs.fields.findIndex(
														(item) => item.id === rowData.id, // or compare other unique fields
													);
													if (index !== -1) {
														plaintiffs.remove(index);
													}
												}}
												onEdit={(rowData: number) => {
													const index = plaintiffs.fields.findIndex((item) => item.id === rowData.id);
													if (index !== -1) {
														const defendantModal = modal.openModal({
															title: `Update ${plaintiffLabel}`,
															description: `Case ${plaintiffLabel}`,
															size: "lg",
															component: (
																<CaseDefendantModal
																	data={rowData}
																	allParties={allParties}
																	emails={allEmails}
																	isPlaintiff={true}
																	isEdit={true}
																	defendantLabel={plaintiffLabel}
																	onSubmit={(updatedData) => {
																		plaintiffs.update(index, updatedData);
																		modal.closeModal(defendantModal);
																	}}
																/>
															),
														});
													}
												}}
											/>
										</div>
									)}
								</div>
							</div>
						</div>
						{hasCounsel && (
							<div className="flex flex-col w-full gap-3">
								<div className="col-span-12 flex items-center text-[14px] text-gray-500">
									<div className="flex flex-col w-full">
										<span className="text-lg font-semibold text-foreground-dark">Law Firm</span>
										<span className="text-base text-foreground/70">Provide the law firm information if plaintiff is represented by a law firm</span>
									</div>
								</div>
								<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="counsel_law_firm"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Law Firm Name</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} className="uppercase" onBlur={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="counsel_law_firm_email"
											render={({ field }) => (
												<FormItem>
													<FormLabel className="flex flex-row w-full gap-1 items-center">
														Law Firm Email
														<Tooltip
															content={
																<div className="flex flex-col w-full gap-1">
																	<div className="text-foreground-dark/90">Email must be valid and active as it will be used for the following:</div>
																	<div className="text-foreground-dark/70 font-medium">• To receive important updates and notifications.</div>
																</div>
															}
															side="top"
														>
															<IconHelpCircle stroke={2} size={20} className="text-primary-600/90" />
														</Tooltip>
													</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="col-span-12 flex items-center text-[14px] text-gray-500">
										<div className="flex flex-col w-full">
											<span className="text-lg font-semibold text-foreground-dark">Law Firm Address</span>
											<span className="text-base text-foreground/70">Fill in the additional details of the Law Firm</span>
										</div>
									</div>
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="lawfirm_region_code"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel>Region</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															// className="uppercase"
															defaultValue={field.value}
															onValueChange={(value) => {
																form.setValue("lawfirm_province_code", "");
																form.setValue("lawfirm_city_code", "");
																setCounselLawFirmRegionName(value.label);
																field.onChange(value.value);
															}}
															returnFullOption={true}
															placeholderValue={{
																label: data?.region?.name,
																value: data?.region?.code,
															}}
															loadOptions={async (value: string) => {
																const items = await regionService.list({
																	"filter[search]": value,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="lawfirm_province_code"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel>Province</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															// className="uppercase"
															defaultValue={field.value}
															disabled={!lawfirmRegionCode}
															key={lawfirmRegionCode}
															onValueChange={(value) => {
																form.setValue("lawfirm_city_code", "");
																field.onChange(value.value);
																setCounselLawFirmProvinceName(value.label);
															}}
															returnFullOption={true}
															placeholderValue={{
																label: data?.province?.name,
																value: data?.province?.code,
															}}
															loadOptions={async (value: string) => {
																const items = await provinceService.list({
																	"filter[search]": value,
																	"filter[region_code]": lawfirmRegionCode,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="lawfirm_city_code"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel>City</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															// className="uppercase"
															defaultValue={field.value}
															disabled={!lawfirmProvinceCode}
															key={lawfirmProvinceCode}
															onValueChange={(value) => {
																field.onChange(value.value);
																setCounselLawFirmCityName(value.label);
															}}
															returnFullOption={true}
															placeholderValue={{
																label: data?.city?.name,
																value: data?.city?.code,
															}}
															loadOptions={async (value: string) => {
																const items = await cityService.list({
																	"filter[search]": value,
																	"filter[province_code]": lawfirmProvinceCode,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="lawfirm_barangay"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Barangay</FormLabel>
													<FormGroup iconPosition="left">
														<Input className="uppercase" autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="lawfirm_address_1"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Street/Lot No./Building/Room No.</FormLabel>
													<FormGroup iconPosition="left">
														<Input className="uppercase" autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-8 flex">
										<FormField
											control={form.control}
											name="lawfirm_address_2"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Apartment/Suite/Unit</FormLabel>
													<FormGroup iconPosition="left">
														<Input className="uppercase" autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-4 flex">
										<FormField
											control={form.control}
											name="lawfirm_zipcode"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Zip Code</FormLabel>
													<FormGroup iconPosition="left">
														<NumericFormat
															decimalScale={2}
															thousandsGroupStyle="thousand"
															thousandSeparator={false}
															className={clsx(
																"block w-full rounded-md border-gray-300 text-gray-500 focus:border-blue-400 focus:ring-blue-400 sm:text-sm",
																"disabled:border-slate-200 disabled:bg-slate-100 disabled:shadow-none",
															)}
															{...field}
															onValueChange={(values) => {
																field.onChange(values.floatValue);
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-12 flex">
										<div className="flex flex-col w-full">
											<span className="text-lg font-semibold text-foreground-dark">Lead Counsel</span>

											<span className="text-base text-foreground/70">Provide the relevant information about the Lead Counsel for the plaintiff.</span>
											{user.hasToken === true && email === null && (
												<span className="mt-2 grid grid-cols-12 gap-3">
													<span className="col-span-3">
														<FormField
															control={form.control}
															name="lead_roll_number"
															render={({ field }) => (
																<FormItem>
																	<FormGroup iconPosition="left">
																		<Input autoComplete="off" {...field} placeholder="Enter roll number to search counsel" />
																	</FormGroup>
																	<FormMessage />
																</FormItem>
															)}
														/>
													</span>
													<span className="col-span-3">
														<Button
															onClick={() => {
																const defendantModal = modal.openModal({
																	title: "Counsel Information",
																	size: "md",
																	component: (
																		<CounselInfoModal
																			rollNumber={form.watch("lead_roll_number")}
																			onSubmit={(data) => {
																				console.log(data.Attorney_GivenName);
																				form.setValue("counsel_first_name", data.Attorney_GivenName);
																				form.setValue("counsel_last_name", data.Attorney_Surname);
																				form.setValue("counsel_middle_name", data.Attorney_MiddleName);
																				form.setValue("counsel_email", data.Email);
																				form.setValue("counsel_suffix", data.Attorney_Suffix);
																				form.setValue("counsel_roll_number", form.watch("lead_roll_number"));
																				modal.closeModal(defendantModal);
																			}}
																		/>
																	),
																});
															}}
														>
															Search Counsel
														</Button>
													</span>
												</span>
											)}
										</div>
									</div>

									{user.hasToken !== true ? (
										<>
											<div className="col-span-12 flex">
												{emailExists && (
													<div className="flex flex-col w-full mt-2">
														<Alert type="info" allowClose={false} title="Email Address Already Exists" icon={<IconAlertCircle size={20} stroke={2} />}>
															The system will use the personal information associated with the email address you entered to auto-fill the form fields. If you do
															not wish to use this information, please enter a different email address.
														</Alert>
													</div>
												)}
											</div>
											<div className="col-span-6 flex">
												<FormField
													control={form.control}
													name="counsel_email"
													render={({ field }) => (
														<FormItem>
															<FormLabel required className="flex flex-row gap-1 items-center">
																Email Address
																<Tooltip
																	content={
																		<div className="flex flex-col w-full gap-1">
																			<div className="text-foreground-dark/90">Email must be valid and active as it will be used for the following:</div>
																			<div className="text-foreground-dark/70 font-medium">
																				• To automatically create an account in our system, unless it's already taken.
																			</div>
																			<div className="text-foreground-dark/70 font-medium">• To set or update an account password.</div>
																			<div className="text-foreground-dark/70 font-medium">• To receive important updates and notifications.</div>
																		</div>
																	}
																	side="top"
																>
																	<IconHelpCircle stroke={2} size={20} className="text-primary-600/90" />
																</Tooltip>
															</FormLabel>
															<FormGroup iconPosition="left">
																<Input autoComplete="off" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-3 flex">
												<FormField
													control={form.control}
													name="counsel_mobile_number"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel>Mobile Number</FormLabel>
															</div>
															<FormGroup iconPosition="left">
																<FormPrepend>+639</FormPrepend>
																<Input
																	{...field}
																	autoComplete="off"
																	placeholder=""
																	maxLength={9}
																	disabled={emailExists}
																	onChange={(e) => {
																		form.trigger("counsel_mobile_number");
																		const onlyNumbers = e.target.value.replace(/\D/g, "");
																		field.onChange(onlyNumbers);
																	}}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-3 flex">
												<FormField
													control={form.control}
													name="counsel_landline"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel>Landline Number</FormLabel>
															</div>
															<FormGroup iconPosition="left">
																<Input
																	{...field}
																	autoComplete="off"
																	placeholder=""
																	maxLength={9}
																	disabled={emailExists}
																	onChange={(e) => {
																		form.trigger("counsel_landline");
																		const onlyNumbers = e.target.value.replace(/\D/g, "");
																		field.onChange(onlyNumbers);
																	}}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-4 flex">
												<FormField
													control={form.control}
													name="counsel_first_name"
													render={({ field }) => (
														<FormItem>
															<FormLabel required>First Name</FormLabel>
															<FormGroup iconPosition="left">
																<Input
																	disabled={emailExists}
																	autoComplete="off"
																	{...field}
																	className="uppercase"
																	onBlur={(e) => field.onChange(e.target.value.toUpperCase())}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-4 flex">
												<FormField
													control={form.control}
													name="counsel_middle_name"
													render={({ field }) => (
														<FormItem>
															<FormLabel>Middle Name</FormLabel>
															<FormGroup iconPosition="left">
																<Input
																	disabled={emailExists}
																	autoComplete="off"
																	{...field}
																	className="uppercase"
																	onBlur={(e) => field.onChange(e.target.value.toUpperCase())}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-3 flex">
												<FormField
													control={form.control}
													name="counsel_last_name"
													render={({ field }) => (
														<FormItem>
															<FormLabel required>Last Name</FormLabel>
															<FormGroup iconPosition="left">
																<Input
																	disabled={emailExists}
																	autoComplete="off"
																	{...field}
																	className="uppercase"
																	onBlur={(e) => field.onChange(e.target.value.toUpperCase())}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-1 flex">
												<FormField
													control={form.control}
													name="counsel_suffix"
													render={({ field }) => (
														<FormItem>
															<FormLabel>Suffix</FormLabel>
															<FormGroup iconPosition="left">
																<Input
																	disabled={emailExists}
																	autoComplete="off"
																	{...field}
																	className="uppercase"
																	onBlur={(e) => field.onChange(e.target.value.toUpperCase())}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										</>
									) : (
										<div className="col-span-12 flex border border-border/50">
											{firstName === null || form.watch("counsel_first_name") === undefined ? (
												<Blanket width="sm">
													<BlanketInfo title="No Data" message="No available data for now. Please check back later or refresh the page." />
												</Blanket>
											) : (
												<DataTable
													data={[
														{
															first_name: form.watch("counsel_first_name"),
															last_name: form.watch("counsel_last_name"),
															middle_name: form.watch("counsel_middle_name"),
															email: form.watch("counsel_email"),
															suffix: form.watch("counsel_suffix"),
															address_1: form.watch("counsel_address_1"),
															address_2: form.watch("counsel_address_2"),
															region_code: form.watch("counsel_region_code"),
															province_code: form.watch("counsel_province_code"),
															city_code: form.watch("counsel_city_code"),
															barangay: form.watch("counsel_barangay"),
															zipcode: form.watch("counsel_zipcode"),
														},
													]}
													columns={counselColumns}
													onRemove={(rowIndex) => {
														form.setValue("counsel_first_name", null);
														form.setValue("counsel_last_name", "");
														form.setValue("counsel_middle_name", "");
														form.setValue("counsel_email", null);
														form.setValue("counsel_suffix", "");
														form.setValue("counsel_address_1", "");
														form.setValue("counsel_address_2", "");
														form.setValue("counsel_region_code", "");
														form.setValue("counsel_province_code", "");
														form.setValue("counsel_city_code", "");
														form.setValue("counsel_barangay", "");
														form.setValue("counsel_zipcode", "");
													}}
												/>
											)}
										</div>
									)}
								</div>
							</div>
						)}

						<div className="flex flex-col w-full gap-3">
							{hasCounsel && (
								<>
									<div className="flex flex-col w-full">
										<span className="text-lg font-semibold text-foreground-dark">Co-counsels</span>
										<span className="text-base text-foreground/70">Provide information of other lawyers representing the plaintiff.</span>
										{user.hasToken === true && (
											<span className="mt-2 grid grid-cols-12 gap-3">
												<span className="col-span-3">
													<FormField
														control={form.control}
														name="roll_number"
														render={({ field }) => (
															<FormItem>
																<FormGroup iconPosition="left">
																	<Input autoComplete="off" {...field} placeholder="Enter roll number to search counsel" />
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</span>

												<span className="col-span-3">
													<Button
														onClick={() => {
															const defendantModal = modal.openModal({
																title: "Counsel Information",
																size: "md",
																component: (
																	<CounselInfoModal
																		rollNumber={form.watch("roll_number")}
																		onSubmit={(data) => {
																			counsels.append({
																				first_name: data.Attorney_GivenName,
																				suffix: data.Attorney_Suffix,
																				last_name: data.Attorney_Surname,
																				middle_name: data.Attorney_MiddleName,
																				email: data.Email,
																				roll_number: form.watch("roll_number"),
																			});
																			modal.closeModal(defendantModal);
																		}}
																	/>
																),
															});
														}}
													>
														Search Counsel
													</Button>
												</span>
											</span>
										)}
									</div>
									<div className="flex flex-col w-full rounded-md border border-border/50">
										<div className="flex flex-col w-full gap-3 border-b last:border-b-0 border-border/50">
											{user.hasToken === false && counsels.fields.length > 0 && (
												<div className="flex flex-row items-center justify-end w-full p-3 gap-4 border-b border-border/50">
													<Button
														size="sm"
														onClick={() => {
															const counselModal = modal.openModal({
																title: "Add New Counsel",
																description: "Case Counsel",
																size: "lg",
																component: (
																	<CaseCoCounselModal
																		lawFirm={allLawFirm}
																		allLawFirmEmail={allLawFirmEmail}
																		allCounsels={allCounsels}
																		lawFirmEmail={counselLawFirmEmail}
																		lawFirmRegion={counselLawFirmRegion}
																		lawFirmProvince={counselLawFirmProvince}
																		lawFirmCity={counselLawFirmCity}
																		lawFirmBarangay={counselLawFirmBarangay}
																		lawFirmAddress1={counselLawFirmAddress1}
																		lawFirmAddress2={counselLawFirmAddress2}
																		lawFirmZipcode={counselLawFirmZipcode}
																		isEdit={false}
																		emails={allEmails}
																		onSubmit={(data) => {
																			counsels.append(data);
																			modal.closeModal(counselModal);
																		}}
																	/>
																),
															});
														}}
													>
														<IconPlus size={14} stroke={2} />
														<span className="">Add Co-Counsel</span>
													</Button>
												</div>
											)}

											<div className="flex flex-col w-full gap-3 border-b last:border-b-0 border-border/50">
												{counsels?.fields.length === 0 ? (
													<Blanket width="sm">
														<BlanketInfo
															title="No Data"
															message="No available data for now. Please check back later or refresh the page."
															action={
																<div>
																	{user.hasToken === false && (
																		<Button
																			size="sm"
																			onClick={() => {
																				const counselModal = modal.openModal({
																					title: "Add New Counsel",
																					description: "Case Counsel",
																					size: "lg",
																					component: (
																						<CaseCoCounselModal
																							lawFirm={allLawFirm}
																							allLawFirmEmail={allLawFirmEmail}
																							allCounsels={allCounsels}
																							lawFirmEmail={counselLawFirmEmail}
																							lawFirmRegion={counselLawFirmRegion}
																							lawFirmProvince={counselLawFirmProvince}
																							lawFirmCity={counselLawFirmCity}
																							lawFirmBarangay={counselLawFirmBarangay}
																							lawFirmAddress1={counselLawFirmAddress1}
																							lawFirmAddress2={counselLawFirmAddress2}
																							lawFirmZipcode={counselLawFirmZipcode}
																							isEdit={false}
																							emails={allEmails}
																							onSubmit={(data) => {
																								counsels.append(data);
																								modal.closeModal(counselModal);
																							}}
																						/>
																					),
																				});
																			}}
																			className="align-right"
																		>
																			<IconPlus size={14} stroke={2} />
																			<span className="">Add Co-Counsel</span>
																		</Button>
																	)}
																</div>
															}
														/>
													</Blanket>
												) : (
													<div className="col-span-12 flex flex-col w-full">
														<DataTable
															data={counsels.fields}
															columns={counselColumns}
															onRowClick={(data: Counsel, rowIndex: number) => {
																const counselModal = modal.openModal({
																	title: "Update Counsel",
																	description: "Case Co-Counsel",
																	size: "lg",
																	component: (
																		<CaseCoCounselModal
																			data={data}
																			lawFirm={allLawFirm}
																			allLawFirmEmail={allLawFirmEmail}
																			allCounsels={allCounsels}
																			lawFirmCity={counselLawFirmCity}
																			isEdit={true}
																			emails={allEmails}
																			onSubmit={(data) => {
																				counsels.update(rowIndex, data);
																				modal.closeModal(counselModal);
																			}}
																		/>
																	),
																});
															}}
															onRemove={(rowIndex) => {
																counsels.remove(rowIndex);
															}}
														/>
													</div>
												)}
											</div>
										</div>
									</div>
								</>
							)}
							<div className="flex flex-col w-full gap-3">
								<div className="flex flex-col w-full">
									<span className="text-lg font-semibold text-foreground-dark">{defendantLabel}/s Information</span>
									<span className="text-base text-foreground/70">
										Provide the full details of the {defendantLabel}(s) in this case. Ensure that all required information is completed for accurate
										record-keeping.
									</span>
								</div>
								<div className="flex flex-col w-full rounded-md border border-border/50">
									{defendants.fields.length > 0 && (
										<div className="flex flex-row items-center justify-end w-full p-3 gap-4 border-b border-border/50">
											<Button
												type="button"
												size="sm"
												onClick={() => {
													const defendantModal = modal.openModal({
														title: `Add New ${defendantLabel}`,
														description: `Case ${defendantLabel}`,
														size: "lg",
														component: (
															<CaseDefendantModal
																emails={allEmails}
																allParties={allParties}
																defendantLabel={defendantLabel}
																onSubmit={(data) => {
																	defendants.append(data);
																	modal.closeModal(defendantModal);
																}}
															/>
														),
													});
												}}
											>
												<IconPlus size={14} stroke={2} />
												<span>Add {defendantLabel}</span>
											</Button>
										</div>
									)}

									<div className="flex flex-col w-full gap-3 border-b last:border-b-0 border-border/50">
										{defendants.fields.length === 0 ? (
											<Blanket width="sm">
												<BlanketInfo
													title="No Data"
													message="No available data for now. Please check back later or refresh the page."
													action={
														<Button
															variant="default"
															size="sm"
															onClick={() => {
																const defendantModal = modal.openModal({
																	title: `Add New ${defendantLabel}`,
																	description: `Case ${defendantLabel}`,
																	size: "lg",
																	component: (
																		<CaseDefendantModal
																			emails={allEmails}
																			allParties={allParties}
																			defendantLabel={defendantLabel}
																			onSubmit={(data) => {
																				defendants.append(data);
																				modal.closeModal(defendantModal);
																			}}
																		/>
																	),
																});
															}}
														>
															<IconPlus size={16} stroke={2} />
															Add {defendantLabel}
														</Button>
													}
												/>
											</Blanket>
										) : (
											<div className="col-span-12 flex flex-col w-full">
												<DataTable
													data={defendants.fields}
													columns={defendantColumns}
													onRowClick={(data: Defendant, rowIndex: number) => {
														const defendantModal = modal.openModal({
															title: `Update ${defendantLabel}`,
															description: `Case ${defendantLabel}`,
															size: "lg",
															component: (
																<CaseDefendantModal
																	data={data}
																	allParties={allParties}
																	emails={allEmails}
																	isEdit={true}
																	defendantLabel={defendantLabel}
																	onSubmit={(updatedData) => {
																		defendants.update(rowIndex, updatedData);
																		modal.closeModal(defendantModal);
																	}}
																/>
															),
														});
													}}
													onRemove={(rowIndex: number) => {
														defendants.remove(rowIndex);
													}}
												/>
											</div>
										)}
									</div>
								</div>
							</div>
							{form.formState.errors.defendants && <p className="text-sm text-danger-600">At least 1 {defendantLabel} is required</p>}
							<div className="flex flex-col w-full gap-3">
								<div className="flex flex-col w-full">
									<span className="text-lg font-semibold text-foreground-dark">Case Title/Particulars</span>
									<span className="text-base text-foreground/70">Case title is auto-generated and can be modified</span>
								</div>
								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="case_title"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Case Title/Particulars</FormLabel>
													<FormDescription />
												</div>
												<FormGroup>
													<Textarea {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
							<div className="flex flex-col w-full gap-3">
								<div className="flex flex-col w-full">
									<span className="text-lg font-semibold text-foreground-dark">Complaint and Annexes</span>
									<span className="text-base text-foreground/70">
										Attach Complaint, Annexes, and other Related Documents. Ensure all files are clear and legible.
									</span>
								</div>
								<div className="flex flex-col w-full">
									<CaseCreateAttachments
										title="Case Complaints"
										hasAttachments={true}
										onFilesChange={async (files) => {
											setFiles(files);
										}}
									/>
									{form.formState.errors.attachments && <p className="text-sm text-danger-600">At least 1 complaint document is required</p>}
								</div>
								<div className="flex flex-col w-full">
									<CaseCreateAttachments
										title="Annexes"
										hasAttachments={true}
										onFilesChange={async (files) => {
											setAnnexFiles(files);
										}}
									/>
								</div>
							</div>
							<div className="flex flex-col w-full gap-3">
								<div className="flex flex-col w-full">
									<span className="text-lg font-semibold text-foreground-dark">Pieces of Evidence</span>
									<span className="text-base text-foreground/70">
										Upload the Documentary Exhibits, Judicial Affidavits, and Other Pieces of Evidence. Ensure all files are clear and legible.
									</span>
								</div>
								<div className="flex flex-col w-full rounded-md border border-border/50">
									<div className="flex flex-col w-full mt-2">
										<Tabs variant="compact" defaultValue="documents" orientation="horizontal">
											<TabsList>
												<TabsTrigger value="documents">Documents</TabsTrigger>
												<TabsTrigger value="testimonial">Testimonial</TabsTrigger>
												<TabsTrigger value="object">Object</TabsTrigger>
											</TabsList>
											<TabsContent value="documents">
												<div className="flex flex-col w-full p-4">
													<EvidenceList
														metadata={{
															evidence_type: "Documentary",
														}}
														schema={[
															{
																className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
																name: "exhibit",
																label: "Exhibit",
																type: "text_input",
																placeholder: "Enter exhibit",
															},
															{
																name: "description",
																label: "Description",
																type: "textarea",
																placeholder: "Enter description",
															},
															{
																name: "purpose",
																label: "Purpose",
																type: "textarea",
																placeholder: "Enter purpose",
															},
														]}
														onAdded={async (files) => {
															form.setValue("evidences", [
																...evidences.filter((e) => e.evidence_type !== "Documentary"),
																...files.map((file) => ({
																	...file,
																	evidence_type: "Documentary",
																})),
															]);
														}}
														data={evidences?.filter((e) => e.evidence_type === "Documentary") || []}
														onRowDelete={async (row) => {
															await expungeCaseMutation.mutateAsync({ id: row.id });
														}}
													/>
												</div>
											</TabsContent>
											{/* Testimonial */}
											<TabsContent value="testimonial">
												<div className="flex flex-col w-full p-4">
													<EvidenceList
														metadata={{
															evidence_type: "Testimonial",
														}}
														data={evidences?.filter((e) => e.evidence_type === "Testimonial") || []}
														schema={[
															// {
															// 	className: "py-2 px-3 text-left text-sm text-foreground-dark",
															// 	name: "exhibit",
															// 	label: "Exhibit",
															// 	type: "text_input",
															// 	placeholder: "Enter exhibit",
															// },
															{
																name: "witness_name",
																label: "Witness Name",
																type: "text_input",
																placeholder: "Enter witness name",
															},
															{
																name: "description",
																label: "Description",
																type: "textarea",
																placeholder: "Enter description",
															},
														]}
														onAdded={async (files) => {
															form.setValue("evidences", [
																...evidences.filter((e) => e.evidence_type !== "Testimonial"),
																...files.map((file) => ({
																	...file,
																	evidence_type: "Testimonial",
																})),
															]);
														}}
														onRowDelete={async (row) => {
															await expungeCaseMutation.mutateAsync({ id: row.id });
														}}
													/>
												</div>
											</TabsContent>
											{/* Object */}
											<TabsContent value="object">
												<div className="flex flex-col w-full">
													<div className="flex flex-col w-full p-4">
														<EvidenceList
															metadata={{
																evidence_type: "Object",
															}}
															data={evidences?.filter((e) => e.evidence_type === "Object") || []}
															schema={[
																{
																	className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
																	name: "exhibit",
																	label: "Exhibit",
																	type: "text_input",
																	placeholder: "Enter exhibit",
																},
																{
																	name: "description",
																	label: "Description",
																	type: "textarea",
																	placeholder: "Enter description",
																},
																{
																	name: "purpose",
																	label: "Purpose",
																	type: "textarea",
																	placeholder: "Enter purpose",
																},
															]}
															onAdded={async (files) => {
																form.setValue("evidences", [
																	...evidences.filter((e) => e.evidence_type !== "Object"),
																	...files.map((file) => ({
																		...file,
																		evidence_type: "Object",
																	})),
																]);
															}}
															onRowDelete={async (row) => {
																await expungeCaseMutation.mutateAsync({ id: row.id });
															}}
														/>
													</div>
												</div>
											</TabsContent>
										</Tabs>
									</div>
								</div>
							</div>

							<div className="flex flex-col w-full gap-3">
								<div className="flex flex-col w-full">
									<span className="text-lg font-semibold text-foreground-dark">Proof of Payment</span>
									<span className="text-base text-foreground/70">Upload proof of any payments made related to this case</span>
								</div>
								<div className="flex flex-col w-full rounded-md border border-border/50">
									<div className="flex flex-row items-center justify-between w-full gap-4 border-b border-border/50 p-4">
										<DragDropFile
											isCaseFiling={true}
											onClear={() => {
												setPayments([]);
											}}
											onSubmit={async (files) => {
												setPayments(files);
											}}
										/>
									</div>
								</div>
							</div>
							{maxSizeExceeded && (
								<Alert type="error" title="Total File Size Limit Exceeded" className="mb-2" icon={<IconAlertCircle size={16} stroke={2} />}>
									{totalMaxSize}
									The combined size of all uploaded files (attachments, evidences, and proof of payment) must not exceed {totalAllowedMb} MB. Please remove some
									files to proceed.
								</Alert>
							)}
						</div>
					</div>
					<div className="flex flex-row w-full gap-2">
						<BottomBar
							isVisible={true}
							onClose={() => {
								modal.openModal({
									type: "confirm",
									variant: "red",
									title: "Confirm Action",
									description: "Are you sure you want to cancel changes?",
									onConfirm: async () => {
										!bcc
											? await navigate({
													to: "/case-classification",
												})
											: await navigate({
													to: "/case-evaluation",
												});
									},
								});
							}}
						>
							{!maxSizeExceeded && (
								<Button type="submit" size="sm" loading={form.formState.isSubmitting || isSubmitting} variant="secondary" onClick={handleConfirmClick}>
									Confirm
								</Button>
							)}
						</BottomBar>
					</div>
				</Form>
			</div>
		</div>
	);
}
